import {
  AppSessionAtomicOperation,
  AppSessionCellId,
  AppSessionId,
  CREATE_APP_SESSION_CELL,
  CellId,
  CellRunMode,
  HexVersionId,
  RUN_CELL,
  assertNever,
  serializeRichText,
  uuid,
} from "@hex/common";

import { AtomicOperationController } from "../atomic-operations/AtomicOperationController";
import { canRunPendingMagicEvent } from "../hex-version-multiplayer/state-hooks/magicEventStateHooks";
import { appSessionMPSelectors } from "../redux/slices/appSessionMPSlice";
import {
  getMagicEventSelectors,
  hexVersionMPSelectors,
} from "../redux/slices/hexVersionMPSlice";
import { AppStore } from "../redux/store";
import { getEditorForCellId } from "../state/models/useEditor";
import { getModel } from "../state/models/useModel";

export interface RunCellArgs {
  store: AppStore;
  dispatchAppSessionAO: AtomicOperationController<AppSessionAtomicOperation>["dispatchAO"];
  hexVersionId: HexVersionId;
  appSessionId: AppSessionId;

  cellId: CellId;

  cellRunMode?: CellRunMode;
  forceOverwriteCache?: boolean;
  executeTrace?: boolean;
  forLogicView?: boolean;
  maybeSkipSql?: boolean;
  fetchPreciseRowCount?: boolean;
}

/**
 * If calling from react, consider `useRunCell`.
 */
export const runCell = async ({
  appSessionId,
  cellId,
  cellRunMode,
  dispatchAppSessionAO,
  executeTrace,
  fetchPreciseRowCount,
  forLogicView,
  forceOverwriteCache,
  hexVersionId,
  maybeSkipSql,
  store,
}: RunCellArgs): Promise<void> => {
  let state = store.getState();
  const cellSelectors = hexVersionMPSelectors.getCellSelectors(hexVersionId);
  const cellContentSelectors =
    hexVersionMPSelectors.getCellContentSelectors(hexVersionId);
  const appSessionCellSelectors =
    appSessionMPSelectors.getAppSessionCellSelectors(appSessionId);
  const magicEventSelectors = getMagicEventSelectors(hexVersionId);

  const cell = cellSelectors.selectById(state, cellId);
  if (cell == null) {
    throw new Error(`Missing state for cell: ${cellId}`);
  }

  const cellContents = cellContentSelectors.selectByCellId(state, cellId);
  if (cellContents == null) {
    throw new Error(`Missing contents for cell: ${cellId}`);
  }

  const magicEvent = cell.latestMagicEventId
    ? magicEventSelectors.selectEntities(state)?.[cell.latestMagicEventId]
    : null;

  // if there is a pending magic edit or fix (ie, not an insert), we'd like to run that event instead of the cell's source
  const magicEventToRun =
    magicEvent && canRunPendingMagicEvent(magicEvent) ? magicEvent : null;

  const userModifiedDiff = state.logicView.cellUserModifiedDiffs[cellId];

  let source: string | undefined = "";
  let linesRun: [number, number] | undefined = undefined;
  let isPendingMagicDiffRun = false;
  switch (cellContents.__typename) {
    case "TextCell": {
      source = serializeRichText(cellContents.richText);
      break;
    }
    case "CodeCell":
    case "SqlCell":
    case "MarkdownCell": {
      // we want to eagerly use the model source to prevent running
      // with stale source if its available since we locally debounce model source updates
      const model = getModel(cellContents.cellId);
      source = model?.getValue() ?? cellContents.source;

      // If it's a SQL cell, we want to check whether there's a selected block of text.
      // If there is a selection, we want to run that instead of the full cell source.
      if ("SqlCell" === cellContents.__typename && model != null) {
        const editor = getEditorForCellId(cellContents.cellId);
        if (editor != null) {
          const selection = editor.getSelection();
          if (selection && !selection.isEmpty()) {
            const selectedText = model.getValueInRange(selection);
            // Only treat as a valid selection if it contains whitespace (more than a single word)
            if (isValidSqlSelection(selectedText)) {
              source = selectedText;
              cellRunMode = CellRunMode.CELL_ONLY;
              linesRun = [selection.startLineNumber, selection.endLineNumber];
            }
          }
        }
      }

      if (magicEventToRun) {
        source = userModifiedDiff ?? magicEventToRun.result ?? undefined;
        cellRunMode = CellRunMode.CELL_ONLY;
      } else if (fetchPreciseRowCount) {
        source = undefined;
        cellRunMode = CellRunMode.CELL_ONLY;
      }

      isPendingMagicDiffRun = !!magicEventToRun;
    }
    // TODO(writeback) - figure this out
    case "WritebackCell":
    case "DisplayTableCell":
    case "MetricCell":
    case "VegaChartCell":
    case "Parameter":
    case "DbtMetricCell":
    case "MapCell":
    case "ChartCell":
    case "PivotCell":
    case "FilterCell":
    case "BlockCell":
    case "ExploreCell":
      break;
    case "CollapsibleCell":
    case "ComponentImportCell":
      return;
    default:
      assertNever(
        cellContents,
        (cellContents as { __typename: string }).__typename,
      );
  }

  let appSessionCell = appSessionCellSelectors.selectByCellId(state, cellId);
  // Normally we don't want to want to create app session cells in the frontend
  // but if this is a legacy appsession that is missing appsessioncells we should create it
  // Alternatively, if the mp model is just out of sync, then this may fail, but we should handle insyncness generally in the controller
  // The last case that could happen is if you run a cell immediately after creating the cell, but before the cell's side-effect to create the appsessioncell is still in flight
  // In this case, this will error as well, but running the button again should fix it. That race condition is also extremely hard to hit in practice
  if (appSessionCell == null) {
    await dispatchAppSessionAO(
      CREATE_APP_SESSION_CELL.create({
        appSessionCellId: uuid() as AppSessionCellId,
        cellId,
      }),
      {
        enableTracing: executeTrace,
      },
    ).promise;

    // refresh the state since we've mutated it with the above operation
    state = store.getState();
  }

  appSessionCell = appSessionCellSelectors.selectByCellId(state, cellId);
  if (appSessionCell == null) {
    throw new Error(`Missing app session cell for cell: ${cellId}`);
  }

  await dispatchAppSessionAO(
    RUN_CELL.create({
      appSessionCellId: appSessionCell.id,
      cellRunMode,
      forceOverwriteCache,
      executeTrace,
      source,
      forLogicView,
      maybeSkipSql,
      isPendingMagicDiffRun,
      linesRun,
      fetchPreciseRowCount,
    }),
    {
      enableTracing: executeTrace,
    },
  ).promise;
};

/**
 * Determines if a text selection should be treated as a valid selection for SQL execution.
 * Only selections containing multiple meaningful words/tokens are considered valid,
 * as single-word selections (even with whitespace) are typically for editing rather than execution.
 */
export function isValidSqlSelection(selectedText: string): boolean {
  if (selectedText.length < 3) {
    return false;
  }

  // Split by whitespace and filter out empty strings to count meaningful tokens
  const meaningfulTokens = selectedText
    .split(/\s+/)
    .filter((token) => token.length > 0);
  return meaningfulTokens.length >= 2;
}
